import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import TransducerSelection from './TransducerSelection';
import BatterySelection from './BatterySelection';
import ShuttleSelection from './ShuttleSelection';
import PoleSelection from './PoleSelection';

const SelectionFlow = () => {
  const navigate = useNavigate();
  const { componentType } = useParams();
  const location = useLocation();
  
  // Get current state from location
  const {
    selectedScreen,
    selectedLivescope,
    selectedOpenWater,
    selectedIceFisher,
    battery,
    shuttle,
    pole,
    transducerType
  } = location.state || {};

  // State for non-screen components
  const [localBattery, setLocalBattery] = useState(battery);

  useEffect(() => {
    setLocalBattery(battery);
  }, [battery]);

  // Navigate back to custom builder with selection
  const handleSelection = (selection, type) => {
    // Create a state object with the current selections
    const newState = {
      selectedScreen: type === 'screen' ? selection : selectedScreen,
      selectedLivescope,
      selectedOpenWater,
      selectedIceFisher,
      battery,
      shuttle,
      pole
    };
    
    // Update the state with the new selection based on type
    if (type === 'livescope') {
      newState.selectedLivescope = selection;
    } else if (type === 'openwater') {
      newState.selectedOpenWater = selection;
    } else if (type === 'icefishing') {
      newState.selectedIceFisher = selection;
    } else if (type === 'battery') {
      newState.battery = selection;
    } else if (type === 'shuttle') {
      newState.shuttle = selection;
    } else if (type === 'pole') {
      newState.pole = selection;
    }
    
    // Navigate back to the custom builder with the updated state
    navigate('/custom-builder', { state: newState });
  };

  // Handle going back without making a selection
  const handleBack = () => {
    navigate('/custom-builder', { 
      state: {
        selectedScreen,
        selectedLivescope,
        selectedOpenWater,
        selectedIceFisher,
        battery,
        shuttle,
        pole
      }
    });
  };



  // Render the appropriate component based on componentType
  const renderComponent = () => {
    switch (componentType) {
      case 'screen':
        // Redirect to the main screen comparison page
        navigate('/compare-screens', { 
          state: {
            selectedScreen,
            selectedLivescope,
            selectedOpenWater,
            selectedIceFisher,
            battery,
            shuttle,
            pole
          },
          replace: true 
        });
        return null;
      case 'transducer':
        if (transducerType === 'liveImaging') {
          return (
            <TransducerSelection
              selectedLivescope={selectedLivescope}
              setSelectedLivescope={(transducer) => handleSelection(transducer, 'livescope')}
              selectedOpenWater={selectedOpenWater}
              setSelectedOpenWater={(transducer) => handleSelection(transducer, 'openwater')}
              selectedIceFisher={selectedIceFisher}
              setSelectedIceFisher={(transducer) => handleSelection(transducer, 'icefishing')}
              prevStep={handleBack}
              nextStep={() => handleBack()}
              transducerType="liveImaging"
            />
          );
        } else if (transducerType === 'openWater') {
          return (
            <TransducerSelection
              selectedLivescope={selectedLivescope}
              setSelectedLivescope={(transducer) => handleSelection(transducer, 'livescope')}
              selectedOpenWater={selectedOpenWater}
              setSelectedOpenWater={(transducer) => handleSelection(transducer, 'openwater')}
              selectedIceFisher={selectedIceFisher}
              setSelectedIceFisher={(transducer) => handleSelection(transducer, 'icefishing')}
              prevStep={handleBack}
              nextStep={() => handleBack()}
              transducerType="openWater"
            />
          );
        } else if (transducerType === 'iceFishing') {
          return (
            <TransducerSelection
              selectedLivescope={selectedLivescope}
              setSelectedLivescope={(transducer) => handleSelection(transducer, 'livescope')}
              selectedOpenWater={selectedOpenWater}
              setSelectedOpenWater={(transducer) => handleSelection(transducer, 'openwater')}
              selectedIceFisher={selectedIceFisher}
              setSelectedIceFisher={(transducer) => handleSelection(transducer, 'icefishing')}
              prevStep={handleBack}
              nextStep={() => handleBack()}
              transducerType="iceFishing"
            />
          );
        } else {
          return (
            <TransducerSelection
              selectedLivescope={selectedLivescope}
              setSelectedLivescope={(transducer) => handleSelection(transducer, 'livescope')}
              selectedOpenWater={selectedOpenWater}
              setSelectedOpenWater={(transducer) => handleSelection(transducer, 'openwater')}
              selectedIceFisher={selectedIceFisher}
              setSelectedIceFisher={(transducer) => handleSelection(transducer, 'icefishing')}
              prevStep={handleBack}
              nextStep={() => handleBack()}
            />
          );
        }
      case 'battery':
        return (
          <BatterySelection
            battery={localBattery}
            setBattery={(newBattery) => {
              setLocalBattery(newBattery);
            }}
            prevStep={handleBack}
            nextStep={() => {
              if (localBattery) {
                handleSelection(localBattery, 'battery');
              } else {
                handleBack();
              }
            }}
            resetAll={() => {
              setLocalBattery(null);
            }}
            jumpToStep={() => {}}
          />
        );
      case 'shuttle':
        return (
          <ShuttleSelection
            shuttle={shuttle}
            setShuttle={(newShuttle) => handleSelection(newShuttle, 'shuttle')}
            prevStep={handleBack}
            nextStep={() => handleBack()}
          />
        );
      case 'pole':
        return (
          <PoleSelection
            pole={pole}
            setPole={(newPole) => handleSelection(newPole, 'pole')}
            prevStep={handleBack}
            nextStep={() => handleBack()}
            isInSelectionFlow={true}
          />
        );
      default:
        return <div>Invalid component type</div>;
    }
  };

  return (
    <div className="selection-flow-container">
      {renderComponent()}
    </div>
  );
};

export default SelectionFlow; 